import React, { useState } from 'react';
import { Sparkles, Loader2, Wand2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Inline AI "Generate / Rewrite" button for admin form fields.
 *
 * Props:
 *  - page (string)            e.g. 'splash' | 'support' | 'lesson' | 'book' | 'legal' | 'ai_coach'
 *  - field (string)           semantic field key e.g. 'hero_subtitle'
 *  - currentValue (string)    the current value of the field
 *  - onApply (fn)             called with the generated string
 *  - length ('short'|'medium'|'long')  default 'medium'
 *  - tone (string)            default 'raw-honest'
 *  - extraContext (object)    optional adjacent fields for cohesion
 *  - label (string)           tooltip / aria label
 *  - size ('sm'|'md')         visual sizing — default 'sm'
 *  - className (string)
 */
export default function AiGenerateButton({
  page,
  field,
  currentValue = '',
  onApply,
  length = 'medium',
  tone = 'raw-honest',
  extraContext,
  label = 'AI rewrite',
  size = 'sm',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [custom, setCustom] = useState('');

  const QUICK_ACTIONS = [
    { id: 'rewrite',   label: 'Rewrite',         instruction: 'Rewrite this so it sounds more polished while keeping the same meaning.' },
    { id: 'shorter',   label: 'Shorter',          instruction: 'Rewrite this much shorter and tighter. Cut every unneeded word.' },
    { id: 'punchier',  label: 'Punchier',         instruction: 'Rewrite this punchier — short sentences, active verbs, real talk. Mickey Trivett HEADSPACE tone.' },
    { id: 'hookier',   label: 'Hookier headline', instruction: 'Rewrite this as a stronger hook — make the reader want to keep reading. Active, vivid, specific.' },
    { id: 'generate',  label: currentValue ? 'Try a fresh take' : 'Generate from scratch', instruction: currentValue ? 'Throw out the current value and write something completely fresh and unexpected (on-brand).' : 'Generate from scratch — on-brand, punchy, Mickey Trivett HEADSPACE voice.' },
  ];

  const runGenerate = async (instruction) => {
    if (!instruction || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post('/admin/ai/generate-field', {
        page,
        field,
        current_value: currentValue || '',
        instruction,
        tone,
        length,
        extra_context: extraContext || {},
      });
      const text = (data?.generated_text || '').trim();
      if (!text) throw new Error('Empty AI response.');
      onApply?.(text);
      toast.success('AI applied.', { description: 'Field updated. Don\u2019t forget to Save.' });
      setOpen(false);
      setCustom('');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'AI generation failed.';
      toast.error('AI generation failed', { description: String(msg).slice(0, 200) });
    } finally {
      setLoading(false);
    }
  };

  const btnSize = size === 'md' ? 'h-9 px-3 text-sm' : 'h-7 px-2 text-xs';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          title={label}
          className={`inline-flex items-center gap-1 ${btnSize} rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[color:var(--kc-gold-2)] hover:bg-[color:var(--kc-gold)] hover:text-black hover:border-[color:var(--kc-gold)] transition-colors font-semibold ${className}`}
          data-testid={`ai-generate-${field}`}
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          AI
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-80 p-0 border border-kc-strong bg-white shadow-2xl"
        data-testid={`ai-generate-popover-${field}`}
      >
        <div className="px-4 py-3 border-b border-kc bg-surface-2">
          <div className="flex items-center gap-2">
            <Wand2 size={14} className="text-[color:var(--kc-gold-2)]" />
            <div className="font-display uppercase text-sm tracking-wider text-ink">AI Generate</div>
          </div>
          <div className="text-[11px] text-muted-kc mt-0.5 truncate">Field: <span className="font-mono">{field}</span></div>
        </div>
        <div className="p-3 flex flex-col gap-1.5">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => runGenerate(a.instruction)}
              disabled={loading}
              className="text-left text-sm px-3 py-2 rounded-md hover:bg-surface-2 disabled:opacity-50 transition-colors text-ink flex items-center justify-between gap-2 border border-transparent hover:border-kc"
              data-testid={`ai-action-${a.id}`}
            >
              <span>{a.label}</span>
              <Sparkles size={12} className="text-[color:var(--kc-gold-2)] opacity-60" />
            </button>
          ))}
        </div>
        <div className="px-3 pb-3">
          <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Custom instruction</label>
          <textarea
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="e.g. Make it more aggressive and add a number"
            className="mt-1 w-full min-h-[60px] p-2 text-sm rounded-md border border-kc-strong focus:outline-none focus:border-[color:var(--kc-gold)]"
            data-testid={`ai-custom-input-${field}`}
          />
          <button
            type="button"
            onClick={() => runGenerate(custom)}
            disabled={loading || !custom.trim()}
            className="mt-2 w-full h-9 inline-flex items-center justify-center gap-2 rounded-md bg-ink text-white text-sm font-semibold hover:bg-black/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid={`ai-custom-run-${field}`}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Generate
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
